/**
 * An administrator's decision on what a family asked to change.
 *
 * Each block is decided on its own — a family that moved and also added a new
 * emergency contact should not have the contact held hostage by a question about
 * the address. Everything defaults to accept, because most requests are simply
 * true, and the work of the screen is spotting the one that isn't.
 *
 * "Current" is read live rather than from the snapshot taken when the parent hit
 * send, so an item the record has moved past since is flagged rather than
 * silently overwritten.
 */
import { useState } from 'react';
import { AlertTriangle, ArrowRight, UserPen } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Label } from '~/components/ui/label';
import { Textarea } from '~/components/ui/textarea';
import { useReviewHouseholdChangeRequest } from './useReviewHouseholdChangeRequest';

export interface ReviewItem {
  key: string;
  label: string;
  currentValue: unknown;
  proposedValue: unknown;
  changedSinceSubmitted: boolean;
}

export interface ReviewRequest {
  id: string;
  submittedAt: unknown;
  submittedByName?: string | null;
  items: ReviewItem[];
}

/**
 * Renders a change-item value as lines of plain text. Values are only ever the
 * address/contact/church/guardian shapes, so a generic renderer beats one
 * component per block here.
 */
export function describeValue(value: unknown): string[] {
  if (value == null) return ['Not set'];
  if (Array.isArray(value)) {
    if (value.length === 0) return ['None'];
    return value.map((entry) => describeValue(entry).join(' · '));
  }
  if (typeof value === 'object') {
    const parts = Object.values(value as Record<string, unknown>)
      .filter((part) => part != null && String(part).trim() !== '')
      .map(String);
    return parts.length > 0 ? [parts.join(', ')] : ['Not set'];
  }
  const text = String(value).trim();
  return text ? [text] : ['Not set'];
}

function ValueBlock({ heading, value, muted }: { heading: string; value: unknown; muted?: boolean }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{heading}</p>
      <div className={`mt-1 space-y-0.5 text-sm ${muted ? 'text-muted-foreground' : 'font-medium'}`}>
        {describeValue(value).map((line, i) => (
          <p key={i} className="break-words">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

export function HouseholdChangeReviewPanel({
  request,
  onReviewed,
}: {
  request: ReviewRequest;
  onReviewed?: () => void;
}) {
  const [accepted, setAccepted] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(request.items.map((item) => [item.key, true])),
  );
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [review, { loading }] = useReviewHouseholdChangeRequest();

  const acceptCount = request.items.filter((item) => accepted[item.key]).length;
  const submittedOn =
    typeof request.submittedAt === 'string'
      ? new Date(request.submittedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
      : '';

  const handleApply = () => {
    setErrors([]);
    void review({
      variables: {
        requestId: request.id,
        decisions: request.items.map((item) => ({ key: item.key, accepted: Boolean(accepted[item.key]) })),
        adminNote: note.trim() || null,
      },
      onCompleted: (result) => {
        const userErrors = result?.reviewHouseholdChangeRequest?.userErrors;
        if (userErrors?.length) {
          setErrors(userErrors.map((e) => e.message));
          return;
        }
        onReviewed?.();
      },
      onError: (error) => setErrors([error.message]),
    });
  };

  return (
    <Card className="border-orange-300 dark:border-orange-900">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserPen className="h-4 w-4" />
          {request.submittedByName ?? 'This family'} asked to change {request.items.length}{' '}
          {request.items.length === 1 ? 'thing' : 'things'}
          {submittedOn && <span className="font-normal text-muted-foreground">· sent {submittedOn}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="divide-y rounded-md border">
          {request.items.map((item) => {
            const isAccepted = Boolean(accepted[item.key]);
            return (
              <div key={item.key} className="space-y-2 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">{item.label}</p>
                  <div className="flex gap-1" role="group" aria-label={`Decision for ${item.label}`}>
                    <Button
                      type="button"
                      size="sm"
                      variant={isAccepted ? 'default' : 'outline'}
                      aria-pressed={isAccepted}
                      onClick={() => setAccepted({ ...accepted, [item.key]: true })}
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={isAccepted ? 'outline' : 'default'}
                      aria-pressed={!isAccepted}
                      onClick={() => setAccepted({ ...accepted, [item.key]: false })}
                    >
                      Decline
                    </Button>
                  </div>
                </div>

                {item.changedSinceSubmitted && (
                  <p className="flex items-center gap-1.5 text-sm text-orange-700 dark:text-orange-400">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    This has changed since the family sent the request — check the current value below.
                  </p>
                )}

                <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap">
                  <ValueBlock heading="Current" value={item.currentValue} muted />
                  <ArrowRight className="mt-5 hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                  <ValueBlock heading="Requested" value={item.proposedValue} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-2">
          <Label htmlFor="household-review-note">Note to the family (optional)</Label>
          <Textarea
            id="household-review-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Anything they should know — especially if you declined something."
            rows={2}
          />
        </div>

        {errors.length > 0 && (
          <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {errors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            Applying {acceptCount} of {request.items.length}.
          </p>
          <Button onClick={handleApply} disabled={loading}>
            {loading ? 'Applying…' : 'Apply decisions'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default HouseholdChangeReviewPanel;
